import { Palette, Settings, Trash2 } from 'lucide-react';
import * as React from 'react';
import styled from 'styled-components';
import { Prompt } from 'react-router';
import { LoadingStateEnum } from '../../import/model';
import { ComponentLoader } from '../../map/modalRight/ModalCalendar';
import { useSetRecoilState } from 'recoil';
import { Field, getFields } from '../../../atoms/forms';
import { ACalculatedFields, CalculatedField, CalculatedFieldTarget, getCalculatedFields, isPromotionCalculatedField } from '../../../atoms/calculatedFields';
import { isOwner, useMe } from '../../../atoms/global/users';
import Add from '../../../components_v2/add/Add';
import MapColorsPopup from '../../../components_v2/colorScale/MapColorsPopup';
import { ButtonStyle } from '../../../components_v2/popup/PopupCreation';
import Restricted from '../../permissions/Restricted';
import { isSuperAdmin } from '../../../components_v2/utils';
import { DarkGreySidely2, RedSidely } from '../../../styles/global/css/Utils';
import { translateToNode, translateToString } from '../../../styles/global/translate';
import { ModalData } from '../../forms/FormTemplateCreator';
import PopupField from '../../forms/PopupField';
import { ToolbarState } from '../../globals/mainPage/mainPage';
import { ColumnDisplayOptions } from '../../map/colorScale';
import { ReportFiltersProvider } from '../../reports/generic/generic';
import { GenericEditorProvider } from '../../reports/generic/genericEditor';
import { syncTagsAndStatuses } from './actions';
import { CalculatedFieldsCreation } from './CalculatedFieldsCreation';
import { getIdListDifferencesWholeKeys, InputStatusName, PointerDiv, SaveButton, SettingIconDiv, StatusContainer, StatusWrapper, TextStatusWrapper, valueToCreationEditionOrDeletion } from './statusesAndTagsSettings';
import { SystemSettingsWrapper, SystemSettingTitle } from './styles';
import { TagType } from '../../../components_v2/toolbarFilter/tags/Model';

// "Champs de campagne": everything a promotion carries, in one place.
// - the promotion fields and the per-product promotion fields: form fields
//   ticked Promotion (and Product), typed in or computed by formula, edited in
//   the field popup, which saves on its own;
// - the calculated fields of the promotions: report-based, computed from the
//   forms filled for each promotion, created with the report editor in its
//   promotion mode (a per-product toggle), saved with the page's save.

const EMPTY_REPORT = JSON.stringify({
	group_by: 'Company',
	panels: [{ columns: [], products: undefined, name: '' }],
	range: null,
	window: null,
	evolution: null,
	variation: null,
	filter: null,
});

export default function PromotionFieldsSettings(props: { setToolBarState: (state: ToolbarState) => void }): JSX.Element {
	const [report, setReport] = React.useState<string>(EMPTY_REPORT);
	return <GenericEditorProvider report={report} setReport={setReport}>
		<ReportFiltersProvider genericReport={JSON.parse(report)}>
			<PromotionFieldsSettingsInner {...props} />
		</ReportFiltersProvider>
	</GenericEditorProvider>;
}

type ColorsEdition = { isOpen: false } | { isOpen: true, title: string, options?: ColumnDisplayOptions, apply: (options: ColumnDisplayOptions | undefined) => void };

function PromotionFieldsSettingsInner(props: { setToolBarState: (state: ToolbarState) => void }): JSX.Element {
	const me = useMe();
	const mayEdit = isOwner(me) || isSuperAdmin();
	const setCalculatedFieldsAtom = useSetRecoilState(ACalculatedFields);
	const [fields, setFields] = React.useState<Field[]>([]);
	const [defaults, setDefaults] = React.useState<CalculatedField[]>([]);
	const [calculated, setCalculated] = React.useState<CalculatedField[]>([]);
	// the field popup: a field to edit, or the domains of a new one
	const [fieldPopup, setFieldPopup] = React.useState<{ key: number, field: ModalData }>();
	const [creationOpen, setCreationOpen] = React.useState(false);
	const [toEdit, setToEdit] = React.useState<CalculatedField | null>(null);
	const [edited, setEdited] = React.useState<CalculatedField | null>(null);
	const [colors, setColors] = React.useState<ColorsEdition>({ isOpen: false });

	const [loading, setLoading] = React.useState(true);
	const load = React.useCallback(async() => {
		const [all, calculatedFields] = await Promise.all([getFields(), getCalculatedFields()]);
		setLoading(false);
		setFields(all.filter(f => f.is_promotion));
		const promotion = calculatedFields.filter(isPromotionCalculatedField);
		setCalculated(promotion);
		setDefaults(JSON.parse(JSON.stringify(promotion)));
		setCalculatedFieldsAtom(calculatedFields);
	}, []);
	React.useEffect(() => { load(); }, [load]);

	// a field created or edited in the creation popup lands in the list, to
	// be saved with the page
	React.useEffect(() => {
		if (!edited) return;
		setCalculated(list => list.some(cf => cf.id === edited.id) ? list.map(cf => cf.id === edited.id ? edited : cf) : [...list, edited]);
	}, [edited]);

	const differences = getIdListDifferencesWholeKeys(defaults, calculated);
	React.useEffect(() => {
		props.setToolBarState({
			bottomRightToolbarComponent: <Restricted to={{ objectAction: 'CreateCompanySettings' }}>
				<SaveButton buttonStyle={ButtonStyle.White} disabled={differences.length === 0} onClick={() => setCalculated(JSON.parse(JSON.stringify(defaults)))}>
					{translateToNode('cancel')}
				</SaveButton>
				<SaveButton
					disabled={differences.length === 0}
					onClick={() => syncTagsAndStatuses({
						field_type: TagType.COMPANY,
						statuses: [],
						tags: [],
						additional_columns: [],
						additional_field_columns: [],
						calculated_fields: differences.map(valueToCreationEditionOrDeletion),
					}).then(load)}
				>
					{translateToNode('save')}
				</SaveButton>
			</Restricted>
		});
	}, [JSON.stringify(differences), defaults]);

	const byName = (a: { name?: string, field_name?: string }, b: { name?: string, field_name?: string }) => (a.name ?? a.field_name ?? '').localeCompare(b.name ?? b.field_name ?? '');
	const promotionFields = fields.filter(f => !f.is_product).sort(byName);
	const productFields = fields.filter(f => f.is_product).sort(byName);
	const alive = calculated.filter(cf => !cf.deleted).sort(byName);

	const fieldRow = (field: Field) => <StatusWrapper key={field.id}>
		<StatusContainer unsync={false} width='450px'>
			<TextStatusWrapper>
				<InputStatusName unsync={false} type='text' value={field.name} nameLength={field.name.length} disabled readOnly />
			</TextStatusWrapper>
			<Kind>{field.formula?.trim() ? `ƒx ${translateToString('form_editor.formula').toLowerCase()}` : field.type}</Kind>
		</StatusContainer>
		{mayEdit && <SettingIconDiv onClick={() => setFieldPopup({ key: Date.now(), field: { field_id: field.id } })}><Settings /></SettingIconDiv>}
	</StatusWrapper>;

	const calculatedRow = (cf: CalculatedField) => <StatusWrapper key={JSON.stringify(cf.id)}>
		<StatusContainer unsync={typeof cf.id !== 'number'} width='450px'>
			<TextStatusWrapper>
				<InputStatusName unsync={typeof cf.id !== 'number'} type='text' value={cf.field_name} nameLength={cf.field_name?.length ?? 0} disabled readOnly />
			</TextStatusWrapper>
			{cf.target === CalculatedFieldTarget.PromotionProduct && <Kind>{translateToString('calculated_fields.per_product')}</Kind>}
		</StatusContainer>
		{mayEdit && <PointerDiv><Trash2 color={RedSidely} onClick={() => { cf.deleted = true; setCalculated([...calculated]); }} /></PointerDiv>}
		{mayEdit && <SettingIconDiv title={translateToString('color_scale.title')} onClick={() => setColors({
			isOpen: true,
			title: cf.field_name,
			options: cf.display_options,
			apply: options => { cf.display_options = options; setCalculated([...calculated]); },
		})}><Palette /></SettingIconDiv>}
		{mayEdit && <SettingIconDiv onClick={() => { setToEdit(cf); setCreationOpen(true); }}><Settings /></SettingIconDiv>}
	</StatusWrapper>;

	if (loading) return <ComponentLoader loadingState={LoadingStateEnum.LOADING} noBackground />;
	return <>
		<Prompt when={differences.length > 0} message={translateToString('unsaved_changes_leave')} />
		<SystemSettingsWrapper>
			<SystemSettingTitle>{translateToNode('promotion_settings.fields')}</SystemSettingTitle>
			{promotionFields.map(fieldRow)}
			{mayEdit && <Add onClick={() => setFieldPopup({ key: Date.now(), field: { is_promotion: true } })} />}
			<br />

			<SystemSettingTitle>{translateToNode('promotion_settings.product_fields')}</SystemSettingTitle>
			{productFields.map(fieldRow)}
			{mayEdit && <Add onClick={() => setFieldPopup({ key: Date.now(), field: { is_promotion: true, is_product: true } })} />}
			<br />

			<SystemSettingTitle>{translateToNode('promotion_settings.calculated_fields')}</SystemSettingTitle>
			{alive.map(calculatedRow)}
			{mayEdit && <Restricted to={{ objectAction: 'CreateCompanySettings' }}>
				<Add onClick={() => { setToEdit(null); setCreationOpen(true); }} />
			</Restricted>}

			{fieldPopup && <PopupField
				key={fieldPopup.key}
				isOpen
				setIsOpen={open => { if (!open) setFieldPopup(undefined); }}
				field={fieldPopup.field}
				onCreate={() => { setFieldPopup(undefined); load(); }}
			/>}
			<CalculatedFieldsCreation
				promotion
				isOpen={creationOpen}
				setIsOpen={open => { setCreationOpen(open); if (!open) setToEdit(null); }}
				setCalculatedField={setEdited}
				calculatedField={toEdit}
			/>
			<MapColorsPopup
				isOpen={colors.isOpen}
				title={colors.isOpen ? colors.title : undefined}
				kind='scale'
				options={colors.isOpen ? colors.options : undefined}
				onClose={() => setColors({ isOpen: false })}
				onSave={options => { if (colors.isOpen) colors.apply(options); }}
			/>
		</SystemSettingsWrapper>
	</>;
}



const Kind = styled.span`
	margin-left: auto;
	padding-right: 12px;
	font-size: 11px;
	color: ${DarkGreySidely2};
	white-space: nowrap;
`;
